
import { cn } from '@/lib/utils';
import { NavItem } from '../../types/navigation';
import { Button } from '../ui/button';
import { RefObject } from 'react';

interface MobileMenuProps {
  menuRef: RefObject<HTMLDivElement>;
  navItems: NavItem[];
  mobileMenuOpen: boolean;
  handleNavigation: (href: string, isExternal?: boolean) => void;
  toggleMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
}

const MobileMenu = ({ 
  menuRef, 
  navItems, 
  mobileMenuOpen, 
  handleNavigation,
  toggleMenu,
  setMobileMenuOpen 
}: MobileMenuProps) => {
  // Add debug logging to check navItems on each render
  console.log('Mobile Menu items:', navItems);
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isInternal?: boolean) => {
    e.preventDefault();
    console.log('Mobile nav item clicked:', href);
    handleNavigation(href, !isInternal && !href.startsWith('/') && !href.startsWith('#'));
  };
  
  return (
    <>
      <div 
        ref={menuRef}
        className={cn(
          "fixed top-0 right-0 h-[100dvh] w-[50%] max-w-[250px] bg-white shadow-xl z-40 transition-transform duration-300 ease-in-out transform overflow-hidden",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          <div className="pt-24 px-6">
            <nav className="flex flex-col space-y-5">
              {navItems.map((item) => {
                // For internal routes like /blog or /portfolio
                if (item.isInternal || (item.href.startsWith('/') && !item.href.includes('#'))) {
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      className="text-elegant-900 text-base font-serif py-1 border-b border-elegant-100 hover:text-elegant-600 transition-colors text-center"
                      onClick={(e) => handleClick(e, item.href, item.isInternal)}
                      data-persistent={item.isPersistent ? 'true' : 'false'}
                    >
                      {item.label}
                    </a>
                  );
                }
                
                // For anchor links like #about
                if (item.href.startsWith('#')) {
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        handleNavigation(item.href);
                        setMobileMenuOpen(false);
                      }}
                      className="text-elegant-900 text-base font-serif py-1 border-b border-elegant-100 hover:text-elegant-600 transition-colors text-center"
                    >
                      {item.label}
                    </button>
                  );
                }
                
                // For external links
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-elegant-900 text-base font-serif py-1 border-b border-elegant-100 hover:text-elegant-600 transition-colors text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                );
              })}
              <Button
                variant="navy"
                className="mt-6 w-full"
                onClick={() => {
                  handleNavigation('/kontakt');
                  setMobileMenuOpen(false);
                }}
              >
                Kontakt
              </Button>
            </nav>
          </div>
          
          <div className="mt-auto p-4 border-t border-elegant-100">
            <p className="text-xs text-elegant-500 text-center">
              © {new Date().getFullYear()} The 321 Portrait
            </p>
          </div>
        </div>
      </div>
      
      <div 
        className={cn(
          "fixed inset-0 bg-elegant-900/25 backdrop-blur-sm z-35 md:hidden transition-opacity duration-300",
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={toggleMenu}
      />
    </>
  );
};

export default MobileMenu;
